

import { ApplicationInsights } from "@microsoft/applicationinsights-web";

let appInsights = null;
let isInitialized = false;


export function initAppInsights() {
  if (isInitialized) return appInsights;

  const connectionString =
    process.env.REACT_APP_APPINSIGHTS_CONNECTION_STRING || "";

  if (!connectionString) {
    // No connection string configured — silently disable tracking.
    // We log once (info, not error) so devs aren't confused.
    // eslint-disable-next-line no-console
    console.info(
      "[appInsights] REACT_APP_APPINSIGHTS_CONNECTION_STRING is not set — telemetry disabled."
    );
    isInitialized = true;
    return null;
  }

  try {
    appInsights = new ApplicationInsights({
      config: {
        connectionString,
        enableAutoRouteTracking: false,
        autoTrackPageVisitTime: true,
        loggingLevelConsole: 1,
        enableCorsCorrelation: false,
        enableRequestHeaderTracking: false,
        enableResponseHeaderTracking: false,
        samplingPercentage: 100,
      },
    });
    appInsights.loadAppInsights();
    isInitialized = true;
    // eslint-disable-next-line no-console
    console.info("[appInsights] Initialized.");
  } catch (err) {
    // Never let telemetry break the app.
    // eslint-disable-next-line no-console
    console.error("[appInsights] Failed to initialize:", err);
    appInsights = null;
    isInitialized = true;
  }

  return appInsights;
}


export function getAppInsights() {
  return appInsights;
}

/**
 * Tag every subsequent event in this browser session as belonging to the given
 * authenticated user and emit a one-off "UserLogin" event. Call this exactly
 * once, right after a successful SSO login (i.e. right after saveUserData()).
 *
 * @param {Object} userData The user object returned by useSession.saveUserData
 *                          ({ id, email, firstName, lastName, fullName, ... })
 */
export function trackLogin(userData) {
  if (!appInsights || !userData) return;
  try {
    // Sanitize the user id: App Insights forbids commas and semicolons.
    const safeUserId = String(userData.id || userData.email || "unknown")
      .replace(/[,;=| ]/g, "_");

    // setAuthenticatedUserContext stamps every subsequent telemetry item with
    // this user id and (optional) account id, so unique-user counts in Azure
    // reflect real SSO identities instead of anonymous cookie ids.
    appInsights.setAuthenticatedUserContext(
      safeUserId,
      userData.email || undefined,
      /* storeInCookie */ true
    );


    appInsights.trackEvent({
      name: "UserLogin",
      properties: {
        userId: safeUserId,
        firstName: userData.firstName || "",
        lastName: userData.lastName || "",
        email: userData.email || "",
        fullName: userData.fullName || "",
      },
    });

    // Flush quickly so the login event isn't lost if the user closes the tab.
    appInsights.flush();
  } catch (err) {
    // Never let telemetry break the app.
    // eslint-disable-next-line no-console
    console.warn("[appInsights] trackLogin failed:", err);
  }
}

/**
 * Track a single SPA route change as a page view. Call this from a useEffect
 * in the top-level routed component whenever location.pathname changes (and
 * only when the user is authenticated, so we don't pollute metrics with the
 * pre-login bounce through /login).
 *
 * @param {string} path The new route path, e.g. "/home" or "/123/cloud-permit"
 */
export function trackPageView(path) {
  if (!appInsights) return;
  try {
    appInsights.trackPageView({
      name: path,
      uri:
        typeof window !== "undefined" && window.location
          ? window.location.href
          : path,
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[appInsights] trackPageView failed:", err);
  }
}

/**
 * Clear the authenticated-user tag when the user logs out so the next user on
 * the same browser doesn't inherit the previous user's identity.
 */
export function clearAuthenticatedUser() {
  if (!appInsights) return;
  try {
    appInsights.clearAuthenticatedUserContext();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[appInsights] clearAuthenticatedUser failed:", err);
  }
}

